import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, LogOut, Phone, User } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const links = [
  { label: "Products", href: "#products" },
  { label: "How It Works", href: "#how-it-works" },
  { label: "Use Cases", href: "#use-cases" },
  { label: "Reviews", href: "#reviews" },
  { label: "Distributors", href: "#distributors" },
  { label: "Contact", href: "#contact" },
];

export const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className="fixed top-0 left-0 right-0 z-50"
      data-testid="main-navbar"
    >
      {/* Top login bar */}
      <AnimatePresence>
        {!scrolled && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="bg-primary text-white overflow-hidden"
            data-testid="top-login-bar"
          >
            <div className="max-w-[1400px] mx-auto px-5 md:px-10 h-9 flex items-center justify-between text-xs">
              <span className="font-monotech tracking-[0.2em] uppercase text-secondary hidden sm:block">
                Your Wound · Our Care
              </span>
              <div className="flex items-center gap-5">
                <a href="tel:+919876543210" className="hidden md:flex items-center gap-1.5 text-white/70 hover:text-secondary transition-colors">
                  <Phone className="w-3 h-3" /> +91 98765 43210
                </a>
                {user ? (
                  <button
                    onClick={logout}
                    data-testid="topbar-logout-btn"
                    className="flex items-center gap-1.5 text-white hover:text-secondary transition-colors"
                  >
                    <LogOut className="w-3 h-3" /> Logout ({user.name})
                  </button>
                ) : (
                  <button
                    onClick={() => navigate("/login")}
                    data-testid="topbar-login-btn"
                    className="flex items-center gap-1.5 text-white hover:text-secondary transition-colors font-semibold"
                  >
                    <User className="w-3 h-3" /> Login / Signup
                  </button>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main nav */}
      <div
        className={`transition-[background-color,border-color,padding] duration-300 ${
          scrolled ? "bg-white/80 backdrop-blur-xl border-b border-border/40 py-3" : "bg-white/60 backdrop-blur-md py-4"
        }`}
      >
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 flex items-center justify-between">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 group">
            <span className="font-heading font-extrabold text-xl md:text-2xl tracking-tighter text-primary">
              Heal<span className="text-secondary">seeal</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className="text-sm font-medium text-text-muted hover:text-primary transition-colors duration-200 relative after:absolute after:left-0 after:-bottom-1 after:h-px after:w-0 after:bg-secondary hover:after:w-full after:transition-[width] after:duration-300"
              >
                {l.label}
              </a>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <Link
              to="/bulk-orders"
              data-testid="nav-b2b-link"
              className="text-sm font-semibold text-primary border border-secondary rounded-full px-5 py-2 hover:bg-secondary hover:text-primary transition-colors duration-200"
            >
              B2B Portal
            </Link>
            {user ? (
              <Button
                variant="outline"
                size="sm"
                onClick={logout}
                data-testid="nav-logout-btn"
                className="rounded-full border-primary/20 text-primary hover:bg-primary hover:text-white gap-1.5"
              >
                <LogOut className="w-3.5 h-3.5" /> Logout
              </Button>
            ) : (
              <Button
                onClick={() => navigate("/login")}
                data-testid="nav-login-btn"
                className="rounded-full bg-primary text-white hover:bg-accent px-6"
              >
                Login / Signup
              </Button>
            )}
          </div>

          <button
            className="lg:hidden text-primary"
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden overflow-hidden bg-white/95 backdrop-blur-xl border-t border-border/40"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {links.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="text-base font-medium text-primary"
                >
                  {l.label}
                </a>
              ))}
              <Link
                to="/bulk-orders"
                onClick={() => setOpen(false)}
                data-testid="nav-mobile-b2b-link"
                className="text-base font-semibold text-secondary-foreground"
              >
                B2B Portal
              </Link>
              {user ? (
                <Button onClick={logout} className="rounded-full bg-primary text-white mt-2">
                  Logout
                </Button>
              ) : (
                <Button
                  onClick={() => navigate("/login")}
                  className="rounded-full bg-primary text-white mt-2"
                >
                  Login / Signup
                </Button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
